"use client"

import * as React from "react"
import SignatureCanvas from "react-signature-canvas"

import { useTranslations } from "@/i18n"
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@ibada/ui"

type ReceiptSignatureDialogProps = {
  open: boolean
  signatureSvg: string | null
  canEdit: boolean
  onOpenChange: (open: boolean) => void
  onSignatureChange: (signatureSvg: string | null) => void
}

/** Draw the signature printed on receipts. Returns it as an SVG data URL wrapping the trimmed drawing. */
export function ReceiptSignatureDialog({
  open,
  signatureSvg,
  canEdit,
  onOpenChange,
  onSignatureChange,
}: ReceiptSignatureDialogProps) {
  const t = useTranslations("finance.receipts")
  const signatureRef = React.useRef<SignatureCanvas | null>(null)
  const signatureCanvasRef = React.useRef<HTMLDivElement | null>(null)
  const [signatureCanvasSize, setSignatureCanvasSize] = React.useState({ width: 0, height: 220 })

  React.useLayoutEffect(() => {
    if (!open) return
    const container = signatureCanvasRef.current
    if (!container) return

    const updateSize = () => {
      const nextWidth = Math.max(container.clientWidth, 1)
      setSignatureCanvasSize((prev) => (prev.width === nextWidth ? prev : { ...prev, width: nextWidth }))
      signatureRef.current?.clear()
    }

    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(container)
    return () => observer.disconnect()
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-xl'>
        <DialogHeader>
          <DialogTitle>{t("signature.title")}</DialogTitle>
          <DialogDescription>{t("signature.description")}</DialogDescription>
        </DialogHeader>
        <div className='overflow-hidden rounded-md border bg-white p-3'>
          <div ref={signatureCanvasRef} className='w-full overflow-hidden'>
            <SignatureCanvas
              ref={signatureRef}
              penColor='#111827'
              canvasProps={{
                width: signatureCanvasSize.width || 520,
                height: signatureCanvasSize.height,
                className: "block",
                style: { touchAction: "none" },
              }}
            />
          </div>
        </div>
        <DialogFooter className='items-center justify-between gap-2 sm:justify-between'>
          <Button type='button' variant='outline' disabled={!canEdit} onClick={() => {
            if (!canEdit) return
            signatureRef.current?.clear()
          }}>
            {t("signature.clear")}
          </Button>
          <div className='flex items-center gap-2'>
            <Button type='button' variant='ghost' onClick={() => onOpenChange(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type='button'
              disabled={!canEdit}
              onClick={() => {
                if (!canEdit) return
                const baseCanvas = signatureRef.current?.getCanvas()
                if (!baseCanvas) return
                const trimmedCanvas = trimSignatureCanvas(baseCanvas)
                const pngDataUrl = trimmedCanvas.toDataURL("image/png")
                const width = trimmedCanvas.width || 1
                const height = trimmedCanvas.height || 1
                const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><image href="${pngDataUrl}" width="${width}" height="${height}" /></svg>`
                const svgBase64 = window.btoa(unescape(encodeURIComponent(svgMarkup)))
                onSignatureChange(`data:image/svg+xml;base64,${svgBase64}`)
                onOpenChange(false)
              }}
            >
              {signatureSvg ? t("signature.save") : t("signature.add")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function trimSignatureCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const context = source.getContext("2d")
  if (!context) return source

  const { width, height } = source
  const imageData = context.getImageData(0, 0, width, height)
  const data = imageData.data
  let top = height
  let bottom = 0
  let left = width
  let right = 0

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4 + 3
      if (data[index] > 0) {
        if (y < top) top = y
        if (y > bottom) bottom = y
        if (x < left) left = x
        if (x > right) right = x
      }
    }
  }

  if (right < left || bottom < top) return source

  const trimmedWidth = right - left + 1
  const trimmedHeight = bottom - top + 1
  const output = document.createElement("canvas")
  output.width = trimmedWidth
  output.height = trimmedHeight
  const outputContext = output.getContext("2d")
  if (!outputContext) return source
  outputContext.putImageData(context.getImageData(left, top, trimmedWidth, trimmedHeight), 0, 0)
  return output
}
